<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod"
import { configure, useForm } from "vee-validate"
import { ref, type Component } from "vue"
import { z } from "zod"
import { ThemeButton, ThemeInput, ThemeTextarea } from "~/components/theme"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/components/ui/form"
import { useCreateHubspotContact } from "~/composables/api"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { titleCase } from "~/lib/case"
import { emailValidator, phoneValidator } from "~/lib/validate"

defineProps<{
    titleAs?: string | Component
}>()

configure({
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false
})

const { location, hasOneLocation } = useLocation()
const { concept } = useConcept()
const { createContact, createContactLoading } = useCreateHubspotContact()
const { formId, portalId } = location.value?.external?.api?.hubspot || {}
const restaurantName = computed(() => hasOneLocation.value ? concept.value?.name : titleCase(concept.value?.name || "") + " " + titleCase(location.value?.name || ""))
const title = computed(() => hasOneLocation.value ? "Contact Us" : "Contact " + restaurantName.value)
const nameLimit = 50
const messageLimit = 500

const formSchema = toTypedSchema(z.object({
    firstName: z.string().min(1, "First name is required").max(nameLimit),
    lastName: z.string().min(1, "Last name is required").max(nameLimit),
    company: z.string().optional(),
    email: emailValidator(),
    phone: phoneValidator(),
    message: z.string().min(1, "Message is required").max(messageLimit),
    formId: z.string().optional(),
    portalId: z.string().optional()
}))

const form = useForm({
    validationSchema: formSchema,
    initialValues: {
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
        message: "",
        formId: formId || "",
        portalId: portalId || ""
    }
})

const isSubmitting = ref(false)
const submitContactError = ref("")
const submitContactSuccess = ref("")

const handleInputPhone = (event: Event) => {
    const input = event.target as HTMLInputElement
    let value = input.value
    value = value.replace(/\D/g, "")
    value = value.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")
    input.value = value
}

const onSubmit = form.handleSubmit(async (values) => {
    isSubmitting.value = true
    try {
        const res = await createContact({
            firstName: values.firstName,
            lastName: values.lastName,
            company: values.company,
            email: values.email,
            phone: values.phone,
            message: values.message,
            formId: values.formId || "",
            portalId: values.portalId || ""
        })

        if (res.errors) {
            submitContactError.value = res.errors
        } else if (res.inlineMessage) {
            submitContactSuccess.value = res.inlineMessage
        } else if (res.redirectUri) {
            submitContactSuccess.value = `Success. Redirecting to ${res.redirectUri}`
            window.location.href = res.redirectUri
        }

        submitContactError.value = ""
        form.resetForm()
    } catch (error) {
        console.error("Error submitting contact form:", error)
        submitContactError.value = "An error occurred while submitting the form. Please contact the restaurant directly."
    } finally {
        isSubmitting.value = false
    }
})
</script>

<template>
    <div class="w-full flex flex-col gap-10 md:gap-12">
        <header class="space-y-4 text-center">
            <component
                :is="titleAs || 'h3'"
                class="text-xl md:text-2xl text-contact-form-title font-headline"
            >
                {{ title }}
            </component>
        </header>
        <form
            class="flex flex-col items-center justify-center gap-8 md:gap-10"
            @submit="onSubmit"
        >
            <div class="w-full grid md:grid-cols-2 gap-x-2 gap-y-8 md:gap-y-10">
                <FormField
                    v-slot="{ componentField }"
                    name="firstName"
                >
                    <FormItem>
                        <FormLabel class="uppercase">
                            First Name <b>*</b>
                        </FormLabel>
                        <FormControl class="text-contact-form-text">
                            <ThemeInput
                                type="text"
                                :maxlength="nameLimit"
                                aria-required="true"
                                class="bg-white/80 [&::placeholder]:text-background/75"
                                v-bind="componentField"
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <FormField
                    v-slot="{ componentField }"
                    name="lastName"
                >
                    <FormItem>
                        <FormLabel class="uppercase">
                            Last Name <b>*</b>
                        </FormLabel>
                        <FormControl class="text-contact-form-text">
                            <ThemeInput
                                type="text"
                                :maxlength="nameLimit"
                                aria-required="true"
                                class="bg-white/80 [&::placeholder]:text-background/75"
                                v-bind="componentField"
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <FormField
                    v-slot="{ componentField }"
                    name="email"
                >
                    <FormItem>
                        <FormLabel class="uppercase">
                            Email <b>*</b>
                        </FormLabel>
                        <FormControl class="text-contact-form-text">
                            <ThemeInput
                                type="email"
                                autocapitalize="off"
                                autocomplete="email"
                                aria-required="true"
                                class="bg-white/80 [&::placeholder]:text-background/75"
                                v-bind="componentField"
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <div>
                    <FormField
                        v-slot="{ componentField }"
                        name="phone"
                    >
                        <FormItem class="col-span-3">
                            <FormLabel class="uppercase">
                                Phone <b>*</b>
                            </FormLabel>
                            <FormControl class="text-contact-form-text">
                                <ThemeInput
                                    type="tel"
                                    autocomplete="tel"
                                    aria-required="true"
                                    class="bg-white/80 [&::placeholder]:text-background/75"
                                    v-bind="componentField"
                                    @input="handleInputPhone"
                                    @change="handleInputPhone"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
            </div>
            <FormField
                v-slot="{ componentField }"
                name="company"
                class="w-full"
            >
                <FormItem class="w-full">
                    <FormLabel class="uppercase">
                        Company
                    </FormLabel>
                    <FormControl class="text-contact-form-text">
                        <ThemeInput
                            type="text"
                            class="bg-white/80 [&::placeholder]:text-background/75"
                            v-bind="componentField"
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            </FormField>
            <FormField
                v-slot="{ componentField }"
                name="message"
            >
                <FormItem class="w-full">
                    <FormLabel class="uppercase">
                        Message <b>*</b>
                    </FormLabel>
                    <FormControl class="text-contact-form-text">
                        <ThemeTextarea
                            :maxlength="messageLimit"
                            placeholder="How can we help you?"
                            aria-required="true"
                            class="bg-white/80 [&::placeholder]:text-contact-form-text/75"
                            v-bind="componentField"
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            </FormField>
            <div
                v-if="Object.keys(form.errors.value).length"
                role="alert"
                aria-live="assertive"
                class="bg-destructive/10 border border-destructive/30 p-4 rounded w-full"
            >
                <p class="text-destructive font-semibold mb-2">
                    Please correct the following errors:
                </p>
                <ul class="list-disc pl-5">
                    <li
                        v-for="(error, field) in form.errors.value"
                        :key="field"
                        class="text-destructive text-sm"
                    >
                        {{ error }}
                    </li>
                </ul>
            </div>
            <ThemeButton
                type="submit"
                size="lg"
                class="w-full md:w-auto border border-background/20 hover:border-background/80 bg-contact-form-submit-background text-contact-form-submit-text hover:bg-contact-form-submit-background/80"
                :loading="createContactLoading"
                :disabled="createContactLoading"
                :tabindex="createContactLoading ? -1 : 0"
                :aria-busy="createContactLoading"
                :aria-label="createContactLoading ? 'Sending message...' : undefined"
            >
                Send Message
            </ThemeButton>
            <p
                v-if="submitContactError"
                role="alert"
                aria-live="assertive"
                class="text-destructive text-sm text-center"
            >
                {{ submitContactError }}
            </p>
            <p
                v-if="submitContactSuccess"
                role="status"
                aria-live="polite"
                class="text-contact-form-success text-sm text-center"
            >
                {{ submitContactSuccess }}
            </p>
        </form>
    </div>
</template>